import { useEffect, useState } from "react";
import { toast } from "sonner";
import api, { API, apiError } from "@/lib/api";
import { Search, Download, Printer, X, CheckCircle2, Ban, RotateCcw } from "lucide-react";

const STATUSES = ["", "PENDING_PAYMENT", "CONFIRMED", "PAYMENT_FAILED", "CANCELLED", "REFUNDED", "EXPIRED"];
const COLOR = {
  CONFIRMED: "bg-emerald-500/15 text-emerald-400", PENDING_PAYMENT: "bg-amber-500/15 text-amber-400",
  PAYMENT_FAILED: "bg-red-500/15 text-red-400", CANCELLED: "bg-slate-500/15 text-slate-400",
  REFUNDED: "bg-purple-500/15 text-purple-400", EXPIRED: "bg-slate-700/40 text-slate-500",
};

export default function AdminReservations() {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [detail, setDetail] = useState(null);

  const load = () => {
    const params = new URLSearchParams();
    if (status) params.set("status", status);
    if (q) params.set("q", q);
    api.get(`/admin/reservations?${params}`).then((r) => setItems(r.data)).catch(() => {});
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const action = async (id, path, label) => {
    if (!window.confirm(`${label} cette réservation ?`)) return;
    try { await api.post(`/admin/reservations/${id}/${path}`); toast.success("Fait"); setDetail(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const exportCsv = async () => {
    const token = localStorage.getItem("almr_token");
    const res = await fetch(`${API}/admin/reservations/export/csv`, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = "reservations.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="admin-reservations">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <h1 className="font-display font-black text-3xl">Réservations</h1>
        <button onClick={exportCsv} data-testid="export-csv-btn" className="btn-ghost !py-2.5"><Download size={16} /> Export CSV</button>
      </div>

      <div className="flex flex-wrap gap-3 mb-6">
        <div className="flex-1 min-w-[220px] relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input data-testid="reservation-search" className="input-dark pl-10" placeholder="Numéro, nom, email…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} />
        </div>
        <select data-testid="reservation-status-filter" className="input-dark max-w-[220px]" value={status} onChange={(e) => setStatus(e.target.value)}>
          {STATUSES.map((s) => <option key={s} value={s}>{s || "Tous les statuts"}</option>)}
        </select>
      </div>

      <div className="card-dark overflow-x-auto">
        {items.length === 0 ? <div className="p-10 text-center text-slate-500 text-sm">Aucune réservation.</div> : (
          <table className="w-full text-sm">
            <thead className="text-left text-slate-500 border-b border-white/10">
              <tr><th className="p-4">Numéro</th><th className="p-4">Client</th><th className="p-4">Service</th><th className="p-4">Date</th><th className="p-4">TTC</th><th className="p-4">Statut</th></tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} onClick={() => setDetail(r)} data-testid={`reservation-row-${r.id}`} className="border-b border-white/5 cursor-pointer hover:bg-white/5">
                  <td className="p-4 font-mono text-gold">{r.booking_number}</td>
                  <td className="p-4">{r.first_name} {r.last_name}</td>
                  <td className="p-4 text-slate-400">{r.service_name}</td>
                  <td className="p-4 text-slate-400">{r.date} {r.start_time}</td>
                  <td className="p-4">{r.price_ttc?.toFixed(2)} €</td>
                  <td className="p-4"><span className={`text-xs font-mono px-2 py-1 rounded-full ${COLOR[r.status] || ""}`}>{r.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {detail && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm no-print" onClick={() => setDetail(null)} />
          <div className="relative card-dark w-full max-w-lg p-8 print-area" data-testid="reservation-detail-modal">
            <button onClick={() => setDetail(null)} className="absolute top-5 right-5 text-slate-400 no-print"><X size={20} /></button>
            <div className="text-center border-b border-white/10 pb-5 mb-5">
              <div className="font-display font-black text-lg">AL MUSIC RECORDS</div>
              <div className="font-mono text-xl text-gold mt-2">{detail.booking_number}</div>
            </div>
            {[["Client", `${detail.first_name} ${detail.last_name}`], ["Email", detail.email], ["Téléphone", detail.phone], ["Service", detail.service_name], ["Date", detail.date], ["Heure", `${detail.start_time} - ${detail.end_time}`], ["Durée", `${detail.duration} h`], ["Prix HT", `${detail.price_ht?.toFixed(2)} €`], ["TVA", `${detail.vat}%`], ["Prix TTC", `${detail.price_ttc?.toFixed(2)} €`], ["Paiement", detail.payment_status], ["Statut", detail.status]].map(([k, v]) => (
              <div key={k} className="flex justify-between text-sm py-1.5"><span className="text-slate-500">{k}</span><span className="text-white">{v}</span></div>
            ))}
            <div className="flex flex-wrap gap-2 mt-6 no-print">
              <button onClick={() => window.print()} className="btn-ghost !py-2 text-xs"><Printer size={14} /> Imprimer</button>
              {detail.status === "PENDING_PAYMENT" && <button onClick={() => action(detail.id, "confirm-manual", "Confirmer")} data-testid="confirm-manual-btn" className="btn-gold !py-2 text-xs"><CheckCircle2 size={14} /> Confirmer (paiement reçu)</button>}
              {["PENDING_PAYMENT", "CONFIRMED"].includes(detail.status) && <button onClick={() => action(detail.id, "cancel", "Annuler")} className="btn-ghost !py-2 text-xs text-red-400"><Ban size={14} /> Annuler</button>}
              {detail.status === "CONFIRMED" && <button onClick={() => action(detail.id, "refund", "Rembourser")} className="btn-ghost !py-2 text-xs"><RotateCcw size={14} /> Rembourser</button>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
